/**
 * Equipment-Host Mapping API Service
 *
 * API functions for linking Warehouse equipment to cloud hosts.
 * Uses Warehouse API for mapping operations.
 * Note: Equipment listing uses getItems() from main services (with unmapped param).
 */

import { ResponseArray, ResponseObject } from '@/types/shared'
import warehouse from '@/utils/axios/warehouse'
import type {
    EquipmentZoneMapping,
    CreateMappingRequest,
    UpdateMappingRequest,
    ItemCapacity,
    AutoMapResult,
    CapacitySnapshot,
} from '../types/mapping'

/**
 * Get equipment-host mappings for a zone
 * @param zoneId Zone UUID
 * @returns List of mappings for the zone
 */
export const getZoneMappings = async (
    zoneId: string
): Promise<EquipmentZoneMapping[]> => {
    const response = await warehouse.get<ResponseArray<EquipmentZoneMapping>>(
        '/capacity/mappings',
        {
            params: { zone_id: zoneId },
        }
    )
    return response.data.results
}

/**
 * Get mapping by host ID
 * @param zoneId Zone UUID
 * @param hostId External host ID
 * @returns Mapping if exists, null otherwise
 */
export const getMappingByHost = async (
    zoneId: string,
    hostId: string
): Promise<EquipmentZoneMapping | null> => {
    const response = await warehouse.get<ResponseArray<EquipmentZoneMapping>>(
        '/capacity/mappings',
        {
            params: { zone_id: zoneId, host_id: hostId },
        }
    )
    const mappings = response.data.results
    return mappings.length > 0 ? mappings[0] : null
}

/**
 * Create equipment-host mapping
 * @param data Mapping creation data
 * @returns Created mapping
 */
export const createMapping = async (
    data: CreateMappingRequest
): Promise<EquipmentZoneMapping> => {
    const response = await warehouse.post<ResponseObject<EquipmentZoneMapping>>(
        '/capacity/mappings',
        data
    )
    return response.data.result
}

/**
 * Delete equipment-host mapping
 * @param id Mapping UUID
 */
export const deleteMapping = async (id: string): Promise<void> => {
    await warehouse.delete(`/capacity/mappings/${id}`)
}

/**
 * Get single mapping detail
 * @param mappingId Mapping UUID
 * @returns Mapping details
 */
export const getMapping = async (
    mappingId: string
): Promise<EquipmentZoneMapping> => {
    const response = await warehouse.get<ResponseObject<EquipmentZoneMapping>>(
        `/capacity/mappings/${mappingId}`
    )
    return response.data.result
}

/**
 * Update equipment-host mapping
 * @param mappingId Mapping UUID
 * @param data Update data
 * @returns Updated mapping
 */
export const updateMapping = async (
    mappingId: string,
    data: UpdateMappingRequest
): Promise<EquipmentZoneMapping> => {
    const response = await warehouse.put<ResponseObject<EquipmentZoneMapping>>(
        `/capacity/mappings/${mappingId}`,
        data
    )
    return response.data.result
}

/**
 * Get mapping snapshots history
 * @param mappingId Mapping UUID
 * @returns List of capacity snapshots
 */
export const getMappingSnapshots = async (
    mappingId: string
): Promise<CapacitySnapshot[]> => {
    const response = await warehouse.get<ResponseArray<CapacitySnapshot>>(
        `/capacity/mappings/${mappingId}/snapshots`
    )
    return response.data.results
}

/**
 * Get item capacity information
 * @param itemId Item UUID (equipment or component)
 * @returns Item capacity data
 */
export const getItemCapacity = async (
    itemId: string
): Promise<ItemCapacity> => {
    const response = await warehouse.get<ResponseObject<ItemCapacity>>(
        `/items/${itemId}/capacity`
    )
    return response.data.result
}

/**
 * Auto-map equipment to zone hosts
 * @param zoneId Zone UUID
 * @returns Auto-mapping result with counts
 */
export const autoMapZone = async (zoneId: string): Promise<AutoMapResult> => {
    const response = await warehouse.post<ResponseObject<AutoMapResult>>(
        `/capacity/zones/${zoneId}/auto-map`
    )
    return response.data.result
}
